import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useAalForSelectedElement, type AalFilters } from '@app/hooks/useAal'
import { AalStat } from './AalStat'

export interface SelectedElementAalProps {
  element_layer_id: string | undefined
  asset_id: number | undefined
  hazard_id: string | undefined
  /**
   * Non-AEP scenario parameters that pin the integration context
   * (e.g. `{slr: 0.5, year: 2050, intervention: 'current_stopbank'}`).
   * Pass `hazardLayer.parameters` — the `aep` key is stripped server-side.
   */
  filters?: AalFilters
}

/**
 * Renders an AAL band for a single (asset, hazard) under a specific scenario
 * context. Self-gated by the `aalReporting` feature flag — safe to drop into
 * any selected-element card without an outer guard.
 */
export const SelectedElementAal = ({
  element_layer_id,
  asset_id,
  hazard_id,
  filters,
}: SelectedElementAalProps) => {
  return (
    <FeatureFlagGate flag={FEATURE_FLAGS.AAL_REPORTING}>
      <SelectedElementAalInner
        element_layer_id={element_layer_id}
        asset_id={asset_id}
        hazard_id={hazard_id}
        filters={filters}
      />
    </FeatureFlagGate>
  )
}

const SelectedElementAalInner = ({
  element_layer_id,
  asset_id,
  hazard_id,
  filters,
}: SelectedElementAalProps) => {
  const { data, isLoading, isError } = useAalForSelectedElement({
    element_layer_id,
    asset_id,
    hazard_id,
    filters,
  })

  if (isError) {
    return <AalStat aal_min={null} aal_max={null} reason="no_damage_ratio_data" />
  }

  return (
    <AalStat
      aal_min={data?.aal_min}
      aal_max={data?.aal_max}
      reason={data?.reason}
      isLoading={isLoading}
    />
  )
}

export default SelectedElementAal
